import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Package, ArrowLeft, Check, Tag, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import {
  getMockTestBundles,
  getMyMockTestPurchases,
  getMyMockTestSubscription,
  type MockTestBundle,
} from "@/api/lms/mock-tests";
import { initBundleBkashPayment } from "@/api/lms/bkash";

export default function MockTestBundlesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const isStudent = user?.role === "student";

  const { data: bundles = [], isLoading } = useQuery({
    queryKey: ["mockTestBundles", "active"],
    queryFn: () => getMockTestBundles(true),
  });

  // The student's purchases + subscription, used to mark bundles as owned.
  const { data: myPurchases = [] } = useQuery({
    queryKey: ["myMockTestPurchases"],
    queryFn: getMyMockTestPurchases,
    enabled: isStudent,
  });
  const { data: mySubscription } = useQuery({
    queryKey: ["myMockTestSubscription"],
    queryFn: getMyMockTestSubscription,
    enabled: isStudent,
  });

  // Test IDs the student still has usable access to: not expired AND with
  // attempts remaining. Once attempts are used up a test drops out of this set,
  // so the bundle can be bought again to top the attempts back up.
  const activeTestIds = useMemo(
    () =>
      new Set(
        myPurchases
          .filter(
            (p) =>
              (!p.expiresAt || new Date(p.expiresAt) > new Date()) &&
              (p.remainingAttempts ?? 0) > 0,
          )
          .map((p) => p.mockTestId),
      ),
    [myPurchases],
  );
  const hasActiveSubscription = !!mySubscription?.active;

  // A bundle counts as "owned" (active) only while an all-access subscription is
  // active, or the student still has remaining attempts on every included test.
  const isBundleOwned = (bundle: MockTestBundle) =>
    hasActiveSubscription ||
    (bundle.items.length > 0 && bundle.items.every((i) => activeTestIds.has(i.mockTestId)));

  // Report the result of a bKash bundle checkout when the gateway redirects back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (!payment) return;
    if (payment === "success") {
      toast.success("Bundle purchased! All included tests are unlocked.");
      // Refresh purchases so the bundle shows as owned.
      void queryClient.invalidateQueries({ queryKey: ["myMockTestPurchases"] });
    } else if (payment === "cancelled") toast.info("Payment cancelled.");
    else if (payment === "failed") toast.error(params.get("reason") || "Payment failed.");
    // Clean the query string so the toast doesn't repeat on refresh.
    window.history.replaceState({}, "", "/mock-test-bundles");
  }, [queryClient]);

  const handleBuy = async (bundle: MockTestBundle) => {
    try {
      setBuyingId(bundle.id);
      const res = await initBundleBkashPayment(bundle.id);
      if (res.bkashURL) {
        toast.success("Redirecting to bKash payment...");
        window.location.href = res.bkashURL;
        return;
      }
      toast.success("Bundle unlocked! All included tests are now available.");
      // Refresh so the card flips to "Owned" without a reload.
      await queryClient.invalidateQueries({ queryKey: ["myMockTestPurchases"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Purchase failed");
    } finally {
      setBuyingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="dash-page">
        <div className="dash-page-header mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2 h-8 shrink-0" asChild>
              <Link to="/mock-tests"><ArrowLeft className="size-4 mr-1" />Mock Tests</Link>
            </Button>
            <div className="min-w-0">
              <h1 className="dash-page-title flex items-center gap-2 truncate">
                <Package className="text-primary size-6 shrink-0" />
                Mock Test Bundles
              </h1>
              <p className="dash-page-subtitle truncate">
                Buy grouped mock tests together at a discounted price
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm p-6">
            <Loader2 className="animate-spin size-4" /> Loading bundles…
          </div>
        ) : bundles.length === 0 ? (
          <KCard className="p-10 text-center text-muted-foreground">
            <Package className="size-10 mx-auto mb-4 opacity-30" />
            <p className="text-sm">No bundles are available right now.</p>
          </KCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {bundles.map((bundle: MockTestBundle) => {
              const saved = Number(bundle.originalPrice) - Number(bundle.discountedPrice);
              const savePct = Number(bundle.originalPrice) > 0
                ? Math.round((saved / Number(bundle.originalPrice)) * 100)
                : 0;
              const owned = isStudent && isBundleOwned(bundle);
              return (
                <KCard key={bundle.id} className="p-5 flex flex-col gap-3">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-base font-bold text-foreground">{bundle.title}</h3>
                      {owned ? (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                          <CheckCircle2 className="size-3" /> Owned
                        </span>
                      ) : savePct > 0 && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                          <Tag className="size-3" /> Save {savePct}%
                        </span>
                      )}
                    </div>
                    {bundle.description && (
                      <p className="text-xs text-muted-foreground mt-1">{bundle.description}</p>
                    )}
                  </div>

                  <div className="rounded-xl border border-border/60 bg-muted/20 p-3 space-y-1.5 flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {bundle.items.length} test{bundle.items.length !== 1 ? "s" : ""} included
                    </div>
                    <ul className="space-y-1">
                      {bundle.items.map((item) => (
                        <li key={item.id} className="flex items-center gap-2 text-xs text-foreground">
                          <Check className="size-3.5 text-emerald-500 shrink-0" />
                          <span className="truncate">{item.mockTest?.title || item.mockTest?.level || "Mock test"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-end justify-between gap-3">
                    <div>
                      {saved > 0 && (
                        <div className="text-xs text-muted-foreground line-through font-mono">
                          {bundle.currency} {Number(bundle.originalPrice).toLocaleString()}
                        </div>
                      )}
                      <div className="text-xl font-bold font-mono text-primary">
                        {bundle.currency} {Number(bundle.discountedPrice).toLocaleString()}
                      </div>
                    </div>
                    {!isStudent ? (
                      <span className="text-[10px] text-muted-foreground">Students only</span>
                    ) : owned ? (
                      <div className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-4 h-10">
                        <CheckCircle2 className="size-4" />
                        {hasActiveSubscription ? "Included in your pass" : "You own this bundle"}
                      </div>
                    ) : (
                      <Button
                        className="rounded-xl font-bold text-xs h-10 px-6"
                        disabled={buyingId === bundle.id}
                        onClick={() => void handleBuy(bundle)}
                      >
                        {buyingId === bundle.id ? <Loader2 className="animate-spin size-3.5 mr-1" /> : null}
                        Buy Bundle
                      </Button>
                    )}
                  </div>
                </KCard>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
